import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAPI } from '../contexts/APIContext';
import logger from '../utils/logger';

const StorySuggestionAlert = ({ onClose }) => {
  const navigate = useNavigate();
  const { apiClient } = useAPI();
  const [suggestedStory, setSuggestedStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(true);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // Auto-dismiss after 15 seconds
    const autoDismissTimer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => {
        onClose?.();
      }, 300); // Wait for fade out animation
    }, 15000);

    // Fetch a random top story (only once)
    const fetchSuggestedStory = async () => {
      // Prevent multiple fetches
      if (hasFetchedRef.current) {
        return;
      }
      
      try {
        hasFetchedRef.current = true;
        setLoading(true);
        
        const response = await apiClient.getStoriverseStories();
        
        if (response.status && response.data && response.data.length > 0) {
          // Pick a random story from the top stories
          const randomIndex = Math.floor(Math.random() * response.data.length);
          const randomStory = response.data[randomIndex];
          setSuggestedStory(randomStory);
        }
      } catch (error) {
        logger.error('Failed to fetch suggested story:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSuggestedStory();

    return () => {
      clearTimeout(autoDismissTimer);
    };
  }, [apiClient]);

  const handlePlayStory = () => {
    if (suggestedStory?.id) {
      sessionStorage.setItem("autoplay_video_story", "true");
      navigate(`/stories/${suggestedStory.id}`);
      onClose?.();
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose?.();
    }, 300);
  };

  if (!isVisible && !suggestedStory && !loading) {
    return null;
  }

  return (
    <div
      className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
    >
      <div
        className="relative overflow-hidden max-w-sm w-full"
        style={{
          fontFamily: "'Fredoka', sans-serif",
          background: '#FFFFFF',
          border: '2px solid rgba(11,26,51,0.08)',
          borderRadius: 24,
          boxShadow: '0px 8px 0px rgba(11,26,51,0.04), 0px 22px 50px rgba(11,26,51,0.18)',
        }}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          aria-label="Close"
          className="absolute top-3 right-3 z-10 flex items-center justify-center transition active:scale-95"
          style={{
            width: 32,
            height: 32,
            background: '#FFFFFF',
            color: '#5A6B85',
            border: '2px solid rgba(11,26,51,0.10)',
            borderRadius: 999,
            boxShadow: '0px 2px 0px rgba(11,26,51,0.05)',
            cursor: 'pointer',
          }}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="relative p-5">
          {loading ? (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center" style={{ background: 'rgba(0,101,248,0.10)', borderRadius: 14 }}>
                <div className="w-5 h-5 rounded-full animate-spin" style={{ border: '2px solid rgba(0,101,248,0.25)', borderTopColor: '#0065F8' }}></div>
              </div>
              <div className="flex-1">
                <div className="h-4 rounded w-3/4 mb-2 animate-pulse" style={{ background: 'rgba(11,26,51,0.08)' }}></div>
                <div className="h-3 rounded w-1/2 animate-pulse" style={{ background: 'rgba(11,26,51,0.06)' }}></div>
              </div>
            </div>
          ) : suggestedStory ? (
            <>
              {/* Header */}
              <div className="flex items-start gap-3 mb-4">
                <div
                  className="flex items-center justify-center flex-shrink-0"
                  style={{ width: 44, height: 44, borderRadius: 14, background: 'linear-gradient(135deg, #0065F8, #00CAFF)', boxShadow: '0px 3px 0px #073B8E' }}
                >
                  <span className="text-xl" role="img" aria-label="sparkles">✨</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 style={{ color: '#0B1A33', fontWeight: 700, fontSize: 18, lineHeight: 1.15, margin: 0 }}>
                    Discover a Story
                  </h3>
                  <p style={{ color: '#5A6B85', fontSize: 13, fontWeight: 500, marginTop: 2 }}>
                    Check out this popular story!
                  </p>
                </div>
              </div>

              {/* Story Preview */}
              <div className="mb-4 overflow-hidden" style={{ background: '#F6F8FC', border: '2px solid rgba(11,26,51,0.06)', borderRadius: 18 }}>
                <div className="aspect-video relative overflow-hidden" style={{ background: 'rgba(0,101,248,0.08)' }}>
                  {suggestedStory.thumbnail_url ? (
                    <img
                      key={suggestedStory.thumbnail_url}
                      src={suggestedStory.thumbnail_url}
                      alt={suggestedStory.name || 'Story cover'}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        logger.warn('Failed to load cover image:', suggestedStory.thumbnail_url);
                      }}
                      onLoad={() => {
                        logger.log('Successfully loaded cover image:', suggestedStory.thumbnail_url);
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-4xl opacity-40">📖</span>
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <h4 className="line-clamp-1" style={{ color: '#0B1A33', fontWeight: 700, fontSize: 14, margin: 0 }}>
                    {suggestedStory.name || 'Untitled Story'}
                  </h4>
                  {suggestedStory.description && (
                    <p className="line-clamp-2" style={{ color: '#5A6B85', fontSize: 12, fontWeight: 500, marginTop: 4 }}>
                      {suggestedStory.description}
                    </p>
                  )}
                </div>
              </div>

              {/* Play Button */}
              <button
                onClick={handlePlayStory}
                className="w-full flex items-center justify-center gap-2 transition active:scale-[0.98]"
                style={{
                  background: '#0065F8',
                  color: '#FFFFFF',
                  fontWeight: 700,
                  fontSize: 16,
                  border: 'none',
                  borderRadius: 999,
                  padding: '13px 16px',
                  boxShadow: '0px 3px 0px #073B8E, 0px 10px 22px rgba(0,101,248,0.25)',
                  cursor: 'pointer',
                }}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
                <span>Play Story</span>
              </button>
            </>
          ) : (
            <div className="text-center py-4" style={{ color: '#5A6B85', fontSize: 14 }}>
              No stories available
            </div>
          )}
        </div>

        {/* Progress bar for auto-dismiss */}
        {suggestedStory && (
          <div className="absolute bottom-0 left-0 right-0 overflow-hidden" style={{ height: 4, background: 'rgba(0,101,248,0.10)' }}>
            <div
              style={{
                height: '100%',
                background: '#0065F8',
                width: '100%',
                animation: 'shrink 15s linear forwards',
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default StorySuggestionAlert;

